import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { DxNumberBoxModule, DxValidatorModule } from 'devextreme-angular';
import { Format } from 'devextreme/localization';
import { ChangeEvent, ContentReadyEvent, FocusInEvent, FocusOutEvent, KeyUpEvent, ValueChangedEvent } from 'devextreme/ui/number_box';
import { GlobalizeNumberPipe } from 'src/app/pipes/globalize/globalize-number.pipe';

/**
 * @description
 * A component that provides a customizable number input using DevExtreme's `dx-number-box`.
 * It allows for bi-directional data binding of numeric values, along with several configurable
 * attributes such as placeholder, size, format, visibility, and read-only mode. The component
 * also emits various events to notify about changes or interactions.
 *
 * The component can be toggled between edit mode and view mode.
 *
 * @selector app-number-box
 * @standalone true
 * @imports GlobalizeNumberPipe, DxNumberBoxModule, DxValidatorModule
 */
@Component({
    selector: 'app-number-box',
    imports: [
        GlobalizeNumberPipe,
        DxNumberBoxModule,
        DxValidatorModule,
    ],
    templateUrl: './number-box.component.html',
    styleUrl: './number-box.component.scss',
	standalone: true
})
export class NumberBoxComponent implements OnInit {

	public customComponent: boolean = false;

	public displayValue: string;
	public showError: boolean = false;

	ngOnChanges() {
		this.updateDisplayValue();
	}

	public updateDisplayValue() {
		// Use your globalizeNumber pipe or method here
		this.displayValue = this.formatNumber(this.value);
	}

	public formatNumber(value: number): string {
		// Replace this with your globalizeNumber implementation
		if (value != null) {
			return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
		}
		return '';
	}

	public parseNumber(value: string): number {
		// Replace this with your parsing logic if needed
		const unformatted = value.replace(/,/g, '');

		const number = parseFloat(unformatted);

		return isNaN(number) ? null : number;
	}

	public onInput(event: any) {
		const inputValue = event.target.value;

		this.value = this.parseNumber(inputValue);

		this.valueChange.emit(this.value);

		if (this.valueChangeEvent === 'input') {
			this.emitValueChanged(event);
		}
	}

	public onChangeEvent(event: any) {
		const inputValue = event.target.value;

		this.value = this.parseNumber(inputValue);
		this.valueChange.emit(this.value);

		if (this.valueChangeEvent === 'change') {
			this.emitValueChanged(event);
		}
		this.onChange.emit(event);
		this.validate();
		this.updateDisplayValue();
	}

	public onFocus(event: any) {
		this.onFocusIn.emit(event);
	}

	public onBlur(event: any) {
		this.onFocusOut.emit(event);
		this.validate();
		this.updateDisplayValue();
	}

	public onKeyUpEvent(event: any) {
		this.onKeyUp.emit(event);
	}

	public emitValueChanged(event: any) {
		this.onValueChanged.emit({ value: this.value });
	}

	public validate() {
		if (this.required) {
			this.showError = this.value == null;
		}
	}

	/**
	 * @description Stores the numeric value entered in the number box.
	 * The value is emitted whenever it changes.
	 * @type {number | null}
	 * @default null
	 */
	private _value: number | null = null;
	/**
	 * @description Getter for the current value of the number box.
	 * @returns {number | null}
	 */
	public get value(): number | null {
		return this._value;
	}
	/**
	 * @description Event emitted when the value changes.
	 * @type {EventEmitter<number | null>}
	 */
	@Output()
	public valueChange: EventEmitter<number | null> = new EventEmitter<number | null>();
	/**
	 * @description Setter for the value of the number box. Emits valueChange when updated.
	 * @param {number | null} value
	 */
	@Input()
	public set value(value: number | null) {
		this._value = value;

		this.valueChange.emit(this._value);
	}

	/**
	 * @description The maximum value accepted by the number box.
	 * @type {number | undefined}
	 * @default undefined
	 */
	private _max: number | undefined;
	/**
	 * @description Getter for the maximum value.
	 * @returns {number | undefined}
	 */
	public get max(): number | undefined {
		return this._max;
	}
	/**
	 * @description Event emitted when the maximum value changes.
	 * @type {EventEmitter<number | undefined>}
	 */
	@Output()
	public maxChange: EventEmitter<number | undefined> = new EventEmitter<number | undefined>();
	/**
	 * @description Setter for the maximum value. Emits maxChange when updated.
	 * @param {number | undefined} value
	 */
	@Input()
	public set max(value: number | undefined) {
		this._max = value;
		this.maxChange.emit(this._max);
	}

	/**
	 * @description The minimum value accepted by the number box.
	 * @type {number | undefined}
	 * @default undefined
	 */
	private _min: number | undefined;

	/**
	 * @description Getter for the minimum value.
	 * @returns {number | undefined}
	 */
	public get min(): number | undefined {
		return this._min;
	}

	/**
	 * @description Event emitted when the minimum value changes.
	 * @type {EventEmitter<number | undefined>}
	 */
	@Output()
	public minChange: EventEmitter<number | undefined> = new EventEmitter<number | undefined>();

	/**
	 * @description Setter for the minimum value. Emits minChange when updated.
	 * @param {number | undefined} value
	 */
	@Input()
	public set min(value: number | undefined) {
		this._min = value;
		this.minChange.emit(this._min);
	}

	/**
	 * @description Placeholder text to display inside the number box.
	 * @type {string}
	 * @default ''
	 */
	private _placeholder: string = '';
	/**
	 * @description Getter for the placeholder text.
	 * @returns {string}
	 */
	public get placeholder(): string {
		return this._placeholder;
	}
	/**
	 * @description Event emitted when the placeholder changes.
	 * @type {EventEmitter<string>}
	 */
	@Output()
	public placeholderChange: EventEmitter<string> = new EventEmitter<string>();
	/**
	 * @description Setter for the placeholder text. Emits placeholderChange when updated.
	 * @param {string} value
	 */
	@Input()
	public set placeholder(value: string) {
		this._placeholder = value;

		this.placeholderChange.emit(this._placeholder);
	}

	/**
	 * @description Height of the number box. Can be a number, a string (for pixels), or a function.
	 * @type {number | Function | string | undefined}
	 * @default undefined
	 */
	private _height: number | Function | string | undefined;
	/**
	 * @description Getter for the height of the number box.
	 * @returns {number | Function | string | undefined}
	 */
	public get height(): number | Function | string | undefined {
		return this._height;
	}
	/**
	 * @description Event emitted when the height changes.
	 * @type {EventEmitter<number | Function | string | undefined>}
	 */
	@Output()
	public heightChange: EventEmitter<number | Function | string | undefined> = new EventEmitter<number | Function | string | undefined>();
	/**
	 * @description Setter for the height. Emits heightChange when updated.
	 * @param {number | Function | string | undefined} value
	 */
	@Input()
	public set height(value: number | Function | string | undefined) {
		this._height = value;

		this.heightChange.emit(this._height);
	}

	/**
	 * @description Width of the number box. Can be a number, a string (for pixels), or a function.
	 * @type {number | Function | string | undefined}
	 * @default undefined
	 */
	private _width: number | Function | string | undefined;
	/**
	 * @description Getter for the width of the number box.
	 * @returns {number | Function | string | undefined}
	 */
	public get width(): number | Function | string | undefined {
		return this._width;
	}
	/**
	 * @description Event emitted when the width changes.
	 * @type {EventEmitter<number | Function | string | undefined>}
	 */
	@Output()
	public widthChange: EventEmitter<number | Function | string | undefined> = new EventEmitter<number | Function | string | undefined>();
	/**
	 * @description Setter for the width. Emits widthChange when updated.
	 * @param {number | Function | string | undefined} value
	 */
	@Input()
	public set width(value: number | Function | string | undefined) {
		this._width = value;

		this.widthChange.emit(this._width);
	}

	/**
	 * @description Determines whether the number box is in edit mode.
	 * @type {boolean}
	 * @default true
	 */
	private _editMode: boolean = true;
	/**
	 * @description Getter for the edit mode state.
	 * @returns {boolean}
	 */
	public get editMode(): boolean {
		return this._editMode;
	}
	/**
	 * @description Event emitted when edit mode changes.
	 * @type {EventEmitter<boolean>}
	 */
	@Output()
	public editModeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	/**
	 * @description Setter for the edit mode. Emits editModeChange when updated.
	 * @param {boolean} value
	 */
	@Input()
	public set editMode(value: boolean) {
		this._editMode = value;

		this.editModeChange.emit(this._editMode);
	}

	/**
	 * @description Determines if the number box is read-only.
	 * @type {boolean}
	 * @default false
	 */
	private _readOnly: boolean = false;
	/**
	 * @description Getter for the read-only state.
	 * @returns {boolean}
	 */
	public get readOnly(): boolean {
		return this._readOnly;
	}
	/**
	 * @description Event emitted when read-only state changes.
	 * @type {EventEmitter<boolean>}
	 */
	@Output()
	public readOnlyChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	/**
	 * @description Setter for the read-only state. Emits readOnlyChange when updated.
	 * @param {boolean} value
	 */
	@Input()
	public set readOnly(value: boolean) {
		this._readOnly = value;

		this.readOnlyChange.emit(this._readOnly);
	}

	/**
	 * @description Determines if the number box is visible.
	 * @type {boolean}
	 * @default true
	 */
	private _visible: boolean = true;
	/**
	 * @description Getter for the visibility of the number box.
	 * @returns {boolean}
	 */
	public get visible(): boolean {
		return this._visible;
	}
	/**
	 * @description Event emitted when visibility changes.
	 * @type {EventEmitter<boolean>}
	 */
	@Output()
	public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	/**
	 * @description Setter for the visibility. Emits visibleChange when updated.
	 * @param {boolean} value
	 */
	@Input()
	public set visible(value: boolean) {
		this._visible = value;

		this.visibleChange.emit(this._visible);
	}

	/**
	 * @description Specifies the format to be used in the number box.
	 * The format can be a DevExtreme format object or a string.
	 * @type {Format | string}
	 * @default "#,##0.####"
	 */
	private _format: Format | string = "#,##0.####";
	/**
	 * @description Getter for the format of the number box.
	 * @returns {Format | string}
	 */
	public get format(): Format | string {
		return this._format;
	}
	/**
	 * @description Event emitted when the format changes.
	 * @type {EventEmitter<Format | string>}
	 */
	@Output()
	public formatChange: EventEmitter<Format | string> = new EventEmitter<Format | string>();
	/**
	 * @description Setter for the format. Emits formatChange when updated.
	 * @param {Format | string} value
	 */
	@Input()
	public set format(value: Format | string) {
		this._format = value;

		this.formatChange.emit(this._format);
	}

	/**
	 * @description Index of the tab stop for the number box.
	 * @type {number}
	 * @default 0
	 */
	private _tabIndex: number = 0;
	/**
	 * @description Getter for the tabIndex of the number box.
	 * @returns {number}
	 */
	public get tabIndex(): number {
		return this._tabIndex;
	}
	/**
	 * @description Event emitted when tabIndex changes.
	 * @type {EventEmitter<number>}
	 */
	@Output()
	public tabIndexChange: EventEmitter<number> = new EventEmitter<number>();
	/**
	 * @description Setter for the tabIndex. Emits tabIndexChange when updated.
	 * @param {number} value
	 */
	@Input()
	public set tabIndex(value: number) {
		this._tabIndex = value;

		this.tabIndexChange.emit(this._tabIndex);
	}

	/**
	 * @description Name of the event that triggers a value change.
	 * @type {string}
	 * @default 'keyup' | change
	 */
	private _valueChangeEvent: string = 'keyup';
	/**
	 * @description Getter for the valueChangeEvent of the number box.
	 * @returns {string}
	 */
	public get valueChangeEvent(): string {
		return this._valueChangeEvent;
	}
	/**
	 * @description Event emitted when valueChangeEvent changes.
	 * @type {EventEmitter<string>}
	 */
	@Output()
	public valueChangeEventChange: EventEmitter<string> = new EventEmitter<string>();
	/**
	 * @description Setter for the valueChangeEvent. Emits valueChangeEventChange when updated.
	 * @param {string} value
	 */
	@Input()
	public set valueChangeEvent(value: string) {
		this._valueChangeEvent = value;

		this.valueChangeEventChange.emit(this._valueChangeEvent);
	}

	/**
	 * @description Determines if the clear button is shown.
	 * @type {boolean}
	 * @default false
	 */
	private _showClearButton: boolean = false;
	/**
	 * @description Getter for the showClearButton state.
	 * @returns {boolean}
	 */
	public get showClearButton(): boolean {
		return this._showClearButton;
	}
	/**
	 * @description Event emitted when showClearButton state changes.
	 * @type {EventEmitter<boolean>}
	 */
	@Output()
	public showClearButtonChange: EventEmitter<boolean> = new EventEmitter<boolean>();
	/**
	 * @description Setter for the showClearButton state. Emits showClearButtonChange when updated.
	 * @param {boolean} value
	 */
	@Input()
	public set showClearButton(value: boolean) {
		this._showClearButton = value;

		this.showClearButtonChange.emit(this._showClearButton);
	}

	/**
	 * @description Specifies whether the field is required.
	 * If true, a validation rule is applied.
	 * @type {boolean}
	 * @default false
	 */
	@Input()
	public required: boolean = false;

	/**
	 * @description Event emitted when the number box value changes.
	 * @type {EventEmitter<ChangeEvent>}
	 */
	@Output()
	public onChange: EventEmitter<ChangeEvent> = new EventEmitter<ChangeEvent>();

	/**
	 * @description Event emitted when the number box content is ready.
	 * @type {EventEmitter<ContentReadyEvent>}
	 */
	@Output()
	public onContentReady: EventEmitter<ContentReadyEvent> = new EventEmitter<ContentReadyEvent>();

	/**
	 * @description Event emitted when the number box gains focus.
	 * @type {EventEmitter<FocusInEvent>}
	 */
	@Output()
	public onFocusIn: EventEmitter<FocusInEvent> = new EventEmitter<FocusInEvent>();

	/**
	 * @description Event emitted when the number box loses focus.
	 * @type {EventEmitter<FocusOutEvent>}
	 */
	@Output()
	public onFocusOut: EventEmitter<FocusOutEvent> = new EventEmitter<FocusOutEvent>();

	/**
	 * @description Event emitted when a key is released while the number box is focused.
	 * @type {EventEmitter<KeyUpEvent>}
	 */
	@Output()
	public onKeyUp: EventEmitter<KeyUpEvent> = new EventEmitter<KeyUpEvent>();

	@Input()
	public formatLoadOnKeyUp: boolean = true;

	/**
	 * @description Event emitted when the value changed in the number box.
	 * @type {EventEmitter<ValueChangedEvent>}
	 */
	// @Output()
	// public onValueChanged: EventEmitter<ValueChangedEvent> = new EventEmitter<ValueChangedEvent>();
	@Output()
	public onValueChanged: EventEmitter<any> = new EventEmitter<any>();

	/**
	 * @description Handles formatting when key events occur.
	 * Resets or applies number formatting based on the key pressed.
	 * @param {KeyUpEvent} evt - The key up event to process.
	 */
	public static formatLoad(evt: KeyUpEvent): void {
		if (evt && evt.event) {
			const multiNumberFormat: string = '#,##0.####';

			// DO NOT DELETE THESE LOGS YET
			// console.error(evt);
			// console.error(`value: ${evt.component.option('value')}`);
			// console.error(`initialValue: ${evt.component.option('initialValue')}`);
			// console.error(`changedValye: ${evt.component.option('changedValue')}`);

			if (
				(evt.event.code === 'Delete' || evt.event.code === 'Backspace' || evt.event.code === 'Digit0') &&
				(!evt.component.option('value') || evt.component.option('value') === 0)
			) {
				evt.component.option('format', '');

				// console.error("Format changed to NONE");

				if (evt.event.code === 'Delete' || evt.event.code === 'Backspace') {
					evt.component.option('value', null);
					// console.error("VALUE SET TO NULL");
				} else {
					setTimeout(() => {
						evt.component.option('value', 0);
					}, null);
				}
			} else if (evt.component.option('format') !== multiNumberFormat) {
				evt.component.option('format', multiNumberFormat);
			}
		}
	}

	public formatLoad(evt: KeyUpEvent): void {
		NumberBoxComponent.formatLoad(evt);
	}

	// private removeNonNumbers = (number) => number.toString().replace(/^\.|[^-?\d\.]|\.(?=.*\.)|^0+(?=\d)/g, '');

	// private removetracingNonNumber = (number) => number.toString().replace(/[^0-9]+$/, '');

	// private addThousandSeparators = (number) =>
	// 	number
	// 		.toString()
	// 		.replace(/\D/g, '')
	// 		.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	// private removeThousandSeparator = (number) => number.toString().replace(/,(?=\d{3})/g, '');

	// private cleanString = (input) => {
	// 	// First, handle the special case of a leading '-'
	// 	let hasLeadingMinus = input.startsWith('-');
	// 	let cleaned = input;




	// public formatNumber(evt: any, value: number) {
	// 	const parsedValue = Number(value.replace(/,/g, '')); // Remove existing commas.


	ngOnInit(): void {
		if (this.formatLoadOnKeyUp) {
			this.onKeyUp.subscribe(this.formatLoad);
		}
	}

	// constructor(
	// 	private _globalizeNumberPipe: GlobalizeNumberPipe,
	// ) {}
}
